import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Divider,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';

interface EmailSenderConfigPanelProps {
  nodeId: string;
  nodeData: any;
  onSave: (nodeId: string, data: any) => void;
  onClose: () => void;
}

const EmailSenderConfigPanel: React.FC<EmailSenderConfigPanelProps> = ({
  nodeId,
  nodeData,
  onSave,
  onClose,
}) => {
  const [toEmail, setToEmail] = useState(nodeData?.toEmail || '');
  const [subject, setSubject] = useState(nodeData?.subject || 'Workflow Notification');
  const [emailError, setEmailError] = useState(false);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  useEffect(() => {
    setToEmail(nodeData?.toEmail || '');
    setSubject(nodeData?.subject || 'Workflow Notification');
    setEmailError(false);
  }, [nodeData]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setToEmail(value);
    if (value && !validateEmail(value)) {
      setEmailError(true);
    } else {
      setEmailError(false);
    }
  };

  const handleSave = () => {
    onSave(nodeId, {
      ...nodeData,
      toEmail,
      subject,
    });
    onClose();
  };

  return (
    <Paper
      sx={{
        position: 'absolute',
        right: 16,
        top: 16,
        width: 350,
        maxHeight: '90vh',
        overflowY: 'auto',
        zIndex: 1000,
        boxShadow: 3,
      }}
    >
      <Box sx={{ p: 2, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Email Sender Configuration
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />
      </Box>

      <Box sx={{ px: 2, pb: 2 }}>
        <TextField
          fullWidth
          label="Recipient Email"
          type="email"
          value={toEmail}
          onChange={handleEmailChange}
          sx={{ mb: 2 }}
          helperText={emailError ? "Please enter a valid email address" : "Email address to send the message to"}
          error={emailError}
          required
        />

        <TextField
          fullWidth
          label="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          sx={{ mb: 2 }}
          helperText="Email subject line"
        />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The email body will be generated from the input connected to this node. The email sender node has no output connections.
        </Typography>

        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={!toEmail || emailError}
          >
            Save
          </Button>
          <Button
            fullWidth
            variant="outlined"
            onClick={onClose}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default EmailSenderConfigPanel;

